import React from 'react';
import { Checkbox } from '../../../CheckBox';
import { ErrorMessage } from '../../../ErrorMessage';
import FormSection from '../../../FormSelection';
import FormSubtitle from '../../../FormSubtitle';
import { DropdownSelect } from '../../../DropdownSelect';
import { TextP } from '../../../Text';
import { TextInput } from '../../../TextInput';

import * as Styled from '../styles';
import { STUDY_LOCATIONS } from './const';

type StudyLocationProps = {
  handleCheck: (value: boolean, field: number | string, key: string) => void
  handleChange: (value: string, field: string) => void
  handleToggle: (value: boolean, field: string) => void
  handleSingleValidation: (value: any, field: string, type?: string) => void
  selectedhealthAuthorityRegion: number[]
  healthAuthorityRegionOptions: { id: number, name: string }[]
  hospital?: { value: string, label: string }
  hospitalList: { value: string, label: string }[]
  building: string
  department: string
  streetAddress: string
  city: string
  postalCode: string
  isOnline: boolean
  errors: { [key: string]: string }
  canEdit: boolean
  hasDirectLink: boolean
  directLinkUrl?: string
}

const StudyLocation = ({
  handleCheck,
  selectedhealthAuthorityRegion,
  healthAuthorityRegionOptions,
  hospital,
  hospitalList,
  building,
  department,
  handleChange,
  streetAddress,
  city,
  postalCode,
  errors,
  isOnline,
  handleToggle,
  canEdit,
  hasDirectLink,
  directLinkUrl,
  handleSingleValidation,
}: StudyLocationProps) => (
  <FormSection title={STUDY_LOCATIONS.TITLE} subText={STUDY_LOCATIONS.SUB_TEXT}>
    <Styled.SectiontWrapper id="health_authority_region">
      <FormSubtitle
        subtitle={STUDY_LOCATIONS.REGION_SECTION.SUB_TITLE}
        link={{
          label: STUDY_LOCATIONS.REGION_SECTION.LINK.LABEL,
          url: STUDY_LOCATIONS.REGION_SECTION.LINK.URL,
        }}
        isRequired
      />
      <TextP lineHeight="1.33rem">
        {STUDY_LOCATIONS.REGION_SECTION.DESCRIPTION}
      </TextP>
      <Styled.CheckboxWrapper>
        <Checkbox
          label="All"
          isChecked={selectedhealthAuthorityRegion.length === healthAuthorityRegionOptions.length}
          onChange={(check) => handleCheck(check, 'All', 'health_authority_region')}
          disabled={!canEdit}
        />
      </Styled.CheckboxWrapper>

      {healthAuthorityRegionOptions.map((option) => (
        <Styled.CheckboxWrapper key={option.name || option.id}>
          <Checkbox
            label={option.name}
            isChecked={selectedhealthAuthorityRegion.includes(option.id)}
            onChange={(check) => handleCheck(check, option.id, 'health_authority_region')}
            disabled={!canEdit}
          />
        </Styled.CheckboxWrapper>
      ))}
      {errors.health_authority_region && <ErrorMessage message={errors.health_authority_region} />}
    </Styled.SectiontWrapper>

    <Styled.SectiontWrapper>
      <FormSubtitle subtitle={STUDY_LOCATIONS.ONLINE_SECTION.SUB_TITLE} margin="small" />
      <Styled.SectionDescriptionWrapper>
        <TextP>{STUDY_LOCATIONS.ONLINE_SECTION.DESCRIPTION}</TextP>
      </Styled.SectionDescriptionWrapper>
      <Checkbox
        isChecked={isOnline}
        label={STUDY_LOCATIONS.ONLINE_SECTION.LABEL}
        onChange={() => handleToggle(!isOnline, 'is_online')}
        labelTheme="secondary"
        textTransform="none"
      />
    </Styled.SectiontWrapper>

    {
      isOnline ? (
        <>
          <Styled.SectiontWrapper id="has_direct_link">
            <FormSubtitle
              subtitle={STUDY_LOCATIONS.ONLINE_STUDY.SUB_TITLE}
              margin="small"
            />
            <Styled.SectionDescriptionWrapper>
              <TextP>{STUDY_LOCATIONS.ONLINE_STUDY.DESCRIPTION}</TextP>
              <Styled.SectionDescriptioBold>
                {STUDY_LOCATIONS.ONLINE_STUDY.DESCRIPTION_TWO}
              </Styled.SectionDescriptioBold>
            </Styled.SectionDescriptionWrapper>
            <Checkbox
              isChecked={hasDirectLink}
              label={STUDY_LOCATIONS.ONLINE_STUDY.LABEL}
              onChange={() => handleToggle(!hasDirectLink, 'has_direct_link')}
              labelTheme="secondary"
              textTransform="none"
            />
          </Styled.SectiontWrapper>
          {hasDirectLink && (
            <Styled.SectiontWrapper id="direct_link" isLast>
              <TextInput
                label={STUDY_LOCATIONS.DIRECT_LINK.LABEL}
                description={STUDY_LOCATIONS.DIRECT_LINK.DESCRIPTION}
                placeholder={STUDY_LOCATIONS.DIRECT_LINK.PLACEHOLDER}
                isPublic={false}
                isRequired
                theme="form"
                textValue={directLinkUrl}
                setTextValue={(value) => handleChange(value, 'direct_link_url')}
                error={errors.direct_link_url}
                disabled={!canEdit}
              />
            </Styled.SectiontWrapper>
          )}
        </>
      ) : (
        <>
          <Styled.SectiontWrapper id="hospital">
            <DropdownSelect
              id="hospital"
              label={STUDY_LOCATIONS.HOSPITAL.LABELS.HOSPITAL}
              description={STUDY_LOCATIONS.HOSPITAL.DESCRIPTIONS.HOSPITAL}
              onChange={(value) => handleChange(value, 'hospital_id')}
              selected={hospital}
              options={hospitalList}
              placeholder={STUDY_LOCATIONS.HOSPITAL.PLACEHOLDER.HOSPITAL}
              error={errors.hospital_id}
              isDisabled={!canEdit}
              theme="form"
              isNotVisible
              isSearchable
            />
          </Styled.SectiontWrapper>

          <Styled.SectiontWrapper id="building">
            <TextInput
              label={STUDY_LOCATIONS.HOSPITAL.LABELS.BUILDING}
              description={STUDY_LOCATIONS.HOSPITAL.DESCRIPTIONS.BUILDING}
              theme="form"
              isRequired={false}
              textValue={building}
              setTextValue={(value) => handleChange(value, 'building')}
              error={errors.building}
              disabled={!canEdit}
              maxLength={120}
            />
          </Styled.SectiontWrapper>
          <Styled.SectiontWrapper id="department">
            <TextInput
              label={STUDY_LOCATIONS.HOSPITAL.LABELS.DEPARTMENT}
              description={STUDY_LOCATIONS.HOSPITAL.DESCRIPTIONS.DEPARTMENT}
              theme="form"
              isRequired={false}
              textValue={department}
              setTextValue={(value) => handleChange(value, 'department')}
              error={errors.department}
              disabled={!canEdit}
              maxLength={120}
            />
          </Styled.SectiontWrapper>
          <Styled.SectiontWrapper id="street_address">
            <TextInput
              label={STUDY_LOCATIONS.HOSPITAL.LABELS.STREET}
              description={STUDY_LOCATIONS.HOSPITAL.DESCRIPTIONS.STREET}
              theme="form"
              isRequired
              textValue={streetAddress}
              setTextValue={(value) => handleChange(value, 'street_address')}
              error={errors.street_address}
              disabled={!canEdit}
              onBlurAction={() => handleSingleValidation(streetAddress, 'street_address', 'address')}
              maxLength={120}
            />
          </Styled.SectiontWrapper>
          <Styled.SectiontWrapper id="city">
            <TextInput
              label={STUDY_LOCATIONS.HOSPITAL.LABELS.CITY}
              description={STUDY_LOCATIONS.HOSPITAL.DESCRIPTIONS.CITY}
              isRequired
              theme="form"
              textValue={city}
              setTextValue={(value) => handleChange(value, 'city')}
              error={errors.city}
              disabled={!canEdit}
              onBlurAction={() => handleSingleValidation(city, 'city', 'address')}
              maxLength={120}
            />
          </Styled.SectiontWrapper>
          <Styled.SectiontWrapper id="postal_code" isLast>
            <TextInput
              label={STUDY_LOCATIONS.HOSPITAL.LABELS.POSTAL_CODE}
              description={STUDY_LOCATIONS.HOSPITAL.DESCRIPTIONS.POSTAL_CODE}
              theme="form"
              isRequired
              textValue={postalCode}
              setTextValue={(value) => handleChange(value, 'postal_code')}
              placeholder={STUDY_LOCATIONS.HOSPITAL.PLACEHOLDER.POST_CODE}
              error={errors.postal_code}
              disabled={!canEdit}
              onBlurAction={() => handleSingleValidation(postalCode, 'postal_code', 'address')}
            />
          </Styled.SectiontWrapper>
        </>
      )
    }

  </FormSection>
);
export default StudyLocation;
